export interface UrlParamMap {
  string: string;
  number: number;
  unsigned: number;
  page: number;
  boolean: boolean;
  array: string[];
  object: Record<string, string>;
}

export type UrlParam<T extends keyof UrlParamMap> = UrlParamMap[T] | undefined;

/* ------------------------------------------------------ */

const replaceParam = <T extends keyof UrlParamMap>(
  type: T,
  paramName: string,
  oldValue: string,
  newValue: UrlParam<T>
) => {
  switch (type) {
    case 'number':
      if (oldValue.replace(',', '.') != newValue) {
        setParamToUrl(type, paramName, newValue, true);
      }
      break;
    case 'unsigned':
    case 'page':
      if (oldValue != newValue) {
        setParamToUrl(type, paramName, newValue, true);
      }
      break;
    case 'boolean':
      if (!['true', 'false'].includes(oldValue)) {
        setParamToUrl(type, paramName, newValue, true);
      }
      break;
  }
};

/* ------------------------------------------------------ */

const handleNumber = <T extends keyof UrlParamMap>(
  type: T,
  paramName: string,
  value: string
) => {
  const float = parseFloat(value.replace(',', '.'));
  const newValue = (isNaN(float) ? undefined : float) as UrlParam<T>;
  replaceParam(type, paramName, value, newValue);
  return newValue;
};

const handleUnsigned = <T extends keyof UrlParamMap>(
  type: T,
  paramName: string,
  value: string
) => {
  const int = parseInt(value);
  const newValue = (isNaN(int) ? undefined : int) as UrlParam<T>;
  replaceParam(type, paramName, value, newValue);
  return newValue;
};

const handlePage = <T extends keyof UrlParamMap>(
  type: T,
  paramName: string,
  value: string
) => {
  const int = parseInt(value);
  const newValue = (isNaN(int) || int < 1 ? 1 : int) as UrlParam<T>;
  replaceParam(type, paramName, value, newValue);
  return newValue;
};

const handleBoolean = <T extends keyof UrlParamMap>(
  type: T,
  paramName: string,
  value: string
) => {
  const newValue = (['true', 'false'].includes(value)
    ? value === 'true'
    : undefined) as UrlParam<T>;
  replaceParam(type, paramName, value, newValue);
  return newValue;
};

/* ------------------------------------------------------ */

export const getParamFromUrl = <T extends keyof UrlParamMap>(
  type: T,
  paramName: string
): UrlParam<T> => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(paramName);

  if (value === null) {
    return undefined;
  }

  switch (type) {
    case 'string':
      return value as UrlParam<T>;
    case 'number':
      return handleNumber(type, paramName, value);
    case 'unsigned':
      return handleUnsigned(type, paramName, value);
    case 'page':
      return handlePage(type, paramName, value);
    case 'boolean':
      return handleBoolean(type, paramName, value);
    case 'array':
    case 'object':
      try {
        return JSON.parse(value);
      } catch (e) {
        return undefined;
      }
  }
};

/* ------------------------------------------------------ */

export const setParamToUrl = <T extends keyof UrlParamMap>(
  type: T,
  paramName: string,
  value: UrlParam<T>,
  replaceHistory?: boolean
) => {
  const params = new URLSearchParams(window.location.search);

  if (value === undefined) {
    params.delete(paramName);
  } else {
    switch (type) {
      case 'string':
      case 'number':
      case 'unsigned':
      case 'page':
      case 'boolean':
        params.set(paramName, `${value}`);
        break;
      case 'array':
      case 'object':
        try {
          params.set(paramName, JSON.stringify(value));
        } catch (e) {
          params.delete(paramName);
        }
        break;
    }
  }

  const newUrl = new URL(window.location.href);
  newUrl.search = params.toString();

  replaceHistory
    ? history.replaceState({}, '', newUrl.toString())
    : history.pushState({}, '', newUrl.toString());
};
